// src/features/admin/UserManagementPage.tsx
// Staff/User management: CRUD, toggle active, reset password

import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, ArrowLeft, Search, Loader2, Pencil, Shield, Key,
  Users, CheckCircle, XCircle, UserPlus, Phone, Mail,
} from 'lucide-react';
import { staffAdminService, doctorAdminService } from '../../api/services';
import type { StaffDto, CreateStaffRequest, UpdateStaffRequest, DoctorAdminDto } from '../../api/types';
import { formatDistanceToNow, parseISO, format } from 'date-fns';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const ROLES = ['Admin', 'Reception', 'Doctor'];
const ROLE_COLORS: Record<string, string> = {
  Admin: 'bg-red-100 text-red-700',
  Reception: 'bg-blue-100 text-blue-700',
  Doctor: 'bg-purple-100 text-purple-700',
  Nurse: 'bg-teal-100 text-teal-700',
};

/* ═══════════════════════════════ MAIN PAGE ═══════════════════════════════ */

export function UserManagementPage() {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterActive, setFilterActive] = useState('');
  const [resetId, setResetId] = useState<number | null>(null);
  const [newPassword, setNewPassword] = useState('');

  const { data: staff, isLoading } = useQuery({
    queryKey: ['admin-staff'],
    queryFn: staffAdminService.getAll,
  });

  const toggleMutation = useMutation({
    mutationFn: (id: number) => staffAdminService.toggleActive(id),
    onSuccess: (d) => {
      toast.success(d.isActive ? 'User activated' : 'User deactivated');
      queryClient.invalidateQueries({ queryKey: ['admin-staff'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const resetPwMutation = useMutation({
    mutationFn: () => staffAdminService.resetPassword(resetId!, { newPassword }),
    onSuccess: () => {
      toast.success('Password reset successfully');
      setResetId(null);
      setNewPassword('');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const filtered = useMemo(() => {
    if (!staff) return [];
    return staff.filter((s) => {
      const q = search.toLowerCase();
      const matchSearch = !q || s.name.toLowerCase().includes(q)
        || s.username.toLowerCase().includes(q)
        || (s.email?.toLowerCase().includes(q));
      const matchRole = !filterRole || s.role === filterRole;
      const matchActive = filterActive === '' || String(s.isActive) === filterActive;
      return matchSearch && matchRole && matchActive;
    });
  }, [staff, search, filterRole, filterActive]);

  // ── Detail/Edit view
  if (selectedId !== null || isAdding) {
    return (
      <UserDetailView
        staffId={selectedId}
        onBack={() => { setSelectedId(null); setIsAdding(false); }}
      />
    );
  }

  // ── List view
  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">User Management</h1>
          <p className="text-sm text-slate-500 mt-1">Manage staff accounts, roles, and access</p>
        </div>
        <button onClick={() => setIsAdding(true)} className="btn btn-primary">
          <UserPlus className="w-4 h-4 mr-2" /> Add User
        </button>
      </div>

      {/* Filters */}
      <div className="card p-3 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div className="relative col-span-2 md:col-span-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input type="search" placeholder="Search name, username, email..." className="input pl-9 w-full text-xs h-8"
              value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <select className="input text-xs h-8" value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
            <option value="">All Roles</option>
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <select className="input text-xs h-8" value={filterActive} onChange={(e) => setFilterActive(e.target.value)}>
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          <div className="flex items-center text-xs text-slate-500">
            {filtered.length} of {staff?.length ?? 0}
          </div>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-brand-600" /></div>
      ) : filtered.length === 0 ? (
        <div className="card p-12 text-center text-slate-500">
          <Users className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="text-sm">No users found</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Doctor Link</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3">Last Login</th>
                <th className="px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((user) => (
                <tr key={user.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center text-xs font-bold shrink-0">
                        {user.name.charAt(0)}
                      </div>
                      <div>
                        <div className="font-medium text-slate-800">{user.name}</div>
                        <div className="text-xs text-slate-400 font-mono">{user.username}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={clsx('text-xs px-2.5 py-1 rounded-full font-medium', ROLE_COLORS[user.role] ?? 'bg-slate-100 text-slate-600')}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-xs text-slate-600">
                      {user.phone && <div className="flex items-center gap-1"><Phone className="w-3 h-3" />{user.phone}</div>}
                      {user.email && <div className="flex items-center gap-1 mt-0.5"><Mail className="w-3 h-3" />{user.email}</div>}
                      {!user.phone && !user.email && <span className="text-slate-400">—</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {user.doctorName ?? <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => toggleMutation.mutate(user.id)}
                      className={clsx('text-xs px-2.5 py-1 rounded-full font-medium transition-colors',
                        user.isActive ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-red-100 text-red-600 hover:bg-red-200'
                      )}
                    >{user.isActive ? 'Active' : 'Inactive'}</button>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {user.lastLoginAt
                      ? <span title={format(parseISO(user.lastLoginAt), 'MMM d, yyyy h:mm a')}>
                          {formatDistanceToNow(parseISO(user.lastLoginAt), { addSuffix: true })}
                        </span>
                      : <span className="text-slate-300">Never</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => setSelectedId(user.id)} className="btn btn-ghost btn-sm" title="Edit">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => { setResetId(user.id); setNewPassword(''); }} className="btn btn-ghost btn-sm" title="Reset Password">
                        <Key className="w-3.5 h-3.5 text-amber-500" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm">
          <div className="card w-full max-w-sm mx-4 p-6 shadow-floating animate-slide-in">
            <h3 className="font-semibold text-slate-900 mb-1 flex items-center gap-2">
              <Key className="w-4 h-4 text-amber-500" /> Reset Password
            </h3>
            <p className="text-xs text-slate-500 mb-4">
              Set a new password for {staff?.find((s) => s.id === resetId)?.name ?? 'this user'}.
            </p>
            <input
              type="password"
              className="input w-full mb-4"
              placeholder="New password (min 6 characters)"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoFocus
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setResetId(null)} className="btn btn-ghost btn-sm">Cancel</button>
              <button
                onClick={() => resetPwMutation.mutate()}
                disabled={newPassword.length < 6 || resetPwMutation.isPending}
                className="btn btn-primary btn-sm"
              >
                {resetPwMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Reset'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════ DETAIL VIEW ═══════════════════════════════ */

function UserDetailView({ staffId, onBack }: {
  staffId: number | null;
  onBack: () => void;
}) {
  const queryClient = useQueryClient();
  const isNew = staffId === null;

  const { data: user, isLoading } = useQuery({
    queryKey: ['admin-staff', staffId],
    queryFn: () => staffAdminService.getById(staffId!),
    enabled: !isNew,
  });

  // Fetch doctors for linking (only active ones without existing staff accounts ideally)
  const { data: doctors = [] } = useQuery({
    queryKey: ['admin-doctors'],
    queryFn: doctorAdminService.getAll,
  });

  if (!isNew && isLoading) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-brand-600" /></div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6">
      <button onClick={onBack} className="btn btn-ghost btn-sm mb-4">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to list
      </button>

      <h1 className="text-2xl font-bold text-slate-800 mb-6">
        {isNew ? 'Add New User' : `Edit: ${user?.name}`}
      </h1>

      <div className="card p-6">
        <UserForm
          user={isNew ? null : user!}
          doctors={doctors}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['admin-staff'] });
            onBack();
          }}
        />
      </div>
    </div>
  );
}

/* ═══════════════════════════════ FORM ═══════════════════════════════ */

function UserForm({ user, doctors, onSuccess }: {
  user: StaffDto | null;
  doctors: DoctorAdminDto[];
  onSuccess: () => void;
}) {
  const isNew = user === null;
  const [form, setForm] = useState<Partial<CreateStaffRequest>>(() =>
    user ? {
      username: user.username, name: user.name, role: user.role,
      doctorId: user.doctorId, phone: user.phone, email: user.email,
      password: '',
    } : { role: 'Reception', password: '' }
  );

  const set = (key: string, value: unknown) => setForm((prev) => ({ ...prev, [key]: value }));

  const saveMutation = useMutation({
    mutationFn: () => {
      if (isNew) {
        return staffAdminService.create(form as CreateStaffRequest);
      } else {
        const { username, password, ...updateFields } = form;
        return staffAdminService.update(user!.id, updateFields as UpdateStaffRequest);
      }
    },
    onSuccess: () => {
      toast.success(isNew ? 'User created' : 'User updated');
      onSuccess();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <form onSubmit={(e) => { e.preventDefault(); saveMutation.mutate(); }}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        {/* Username — only for new */}
        {isNew && (
          <div>
            <label className="label">Username *</label>
            <input className="input w-full" required minLength={3} placeholder="e.g. drjones"
              value={form.username ?? ''} onChange={(e) => set('username', e.target.value)} />
          </div>
        )}
        {!isNew && (
          <div>
            <label className="label">Username</label>
            <input className="input w-full bg-slate-50 cursor-not-allowed" disabled value={user?.username ?? ''} />
          </div>
        )}

        {/* Password — only for new */}
        {isNew && (
          <div>
            <label className="label">Password *</label>
            <input type="password" className="input w-full" required minLength={6} placeholder="Min 6 characters"
              value={form.password ?? ''} onChange={(e) => set('password', e.target.value)} />
          </div>
        )}

        <div className={isNew ? 'md:col-span-2' : ''}>
          <label className="label">Full Name *</label>
          <input className="input w-full" required value={form.name ?? ''} onChange={(e) => set('name', e.target.value)} />
        </div>

        <div>
          <label className="label">Role *</label>
          <div className="relative">
            <Shield className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <select className="input w-full pl-9" required value={form.role ?? ''}
              onChange={(e) => set('role', e.target.value)}>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        </div>

        {/* Doctor link — only when role is Doctor */}
        {form.role === 'Doctor' && (
          <div>
            <label className="label">Link to Doctor Profile</label>
            <select className="input w-full" value={form.doctorId ?? ''}
              onChange={(e) => set('doctorId', e.target.value ? Number(e.target.value) : undefined)}>
              <option value="">None</option>
              {doctors.filter(d => d.isActive).map((d) => (
                <option key={d.id} value={d.id}>{d.title ? `${d.title} ` : ''}{d.name} — {d.departmentName}</option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="label">Phone</label>
          <input type="tel" className="input w-full" placeholder="Optional"
            value={form.phone ?? ''} onChange={(e) => set('phone', e.target.value)} />
        </div>

        <div>
          <label className="label">Email</label>
          <input type="email" className="input w-full" placeholder="Optional"
            value={form.email ?? ''} onChange={(e) => set('email', e.target.value)} />
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <button type="submit" disabled={saveMutation.isPending} className="btn btn-primary">
          {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          {isNew ? 'Create User' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
}
